import { Reorder } from 'framer-motion';
import React, { RefObject } from 'react';
import { useIntersection } from 'react-use';
import { SideNavItem, SideNavMode } from './SideNav';
import { SideNavElement } from './SideNavElement';

import { useTranslation } from 'react-i18next';

export const ReorderSideBar = ({
  isExpanded,
  setDragging,
  mode,
  setActiveSubMenu,
  setAllowHoverSubMenu,
  activeSubMenu,
  items,
  updateNavItems,
  editMode,
  onToggleHidden,
}: {
  setDragging: (value: boolean) => void;
  isExpanded?: boolean;
  mode: SideNavMode;
  activeSubMenu?: string;
  items: SideNavItem[];
  setActiveSubMenu: (value?: string) => void;
  setAllowHoverSubMenu: (value?: string) => void;
  updateNavItems: (values: SideNavItem[]) => void;
  /** MI-212 edit mode: rows grow a hide control. */
  editMode?: boolean;
  onToggleHidden?: (key: string) => void;
}) => {
  const { t } = useTranslation('containerInterface');
  const intersectionRef = React.useRef<HTMLAnchorElement>(null);
  // A drag release also fires a click on the row, which would navigate to
  // whatever you were dragging. Swallow clicks that land just after a drag.
  const lastDragEnd = React.useRef(0);
  const intersection = useIntersection(
    intersectionRef as RefObject<HTMLElement>, // TODO: Fix this when react-use is updated to support React 19 properly
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.6,
    },
  );

  return (
    <React.Fragment>
      <Reorder.Group
        id="side-nav-container"
        onReorder={updateNavItems}
        values={items}
        axis="y"
        className={`scrollbar-hide flex flex-col gap-y-2 overflow-y-scroll`}
      >
        {items.map((item, index) => {
          return (
            <Reorder.Item
              transition={{ duration: 0.1, type: 'keyframes' }}
              value={item}
              key={item.key}
              // Reordering is something you go into edit mode to do. Outside
              // it a drag on a nav item was almost always a missed click.
              dragListener={editMode}
              onDrag={() => setDragging(true)}
              onDragEnd={() => {
                lastDragEnd.current = Date.now();
                setDragging(false);
              }}
            >
              <div
                className="relative"
                onClickCapture={(event) => {
                  if (Date.now() - lastDragEnd.current < 250) {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                }}
              >
                {/* Edit mode is for arranging, not navigating: the row's
                    link goes inert so a drag release can never route you to
                    the page you were holding. The eye stays clickable — it
                    sits outside this wrapper. */}
                <div className={editMode ? 'pointer-events-none' : ''}>
                  <SideNavElement
                    setActiveSubMenu={setActiveSubMenu}
                    setAllowHoverSubMenu={setAllowHoverSubMenu}
                    isExpanded={isExpanded}
                    item={item}
                    mode={mode}
                    activeSubMenu={activeSubMenu}
                    linkRef={
                      index === items.length - 1 ? intersectionRef : undefined
                    }
                  />
                </div>
                {editMode && onToggleHidden && item.key !== 'home' && (
                  <button
                    type="button"
                    title={t('hideUnderTheGroup')}
                    className="absolute top-1/2 right-0 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onToggleHidden(item.key);
                    }}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </Reorder.Item>
          );
        })}
      </Reorder.Group>
      <div
        className={`pointer-events-none fixed right-0 bottom-[60px] left-0 h-6 w-[230px] bg-gradient-to-t from-gray-200 via-transparent to-transparent transition-all duration-200 ease-out dark:from-gray-900 ${
          !intersection?.isIntersecting && isExpanded
            ? 'opacity-90'
            : 'opacity-0'
        }`}
      ></div>
    </React.Fragment>
  );
};
